import { FaSyncAlt, FaExclamationCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Badge, ProgressBar, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFetch, useMutate } from "../../../hooks/queries";
import SyncTabsHeader from "../../../Components/data-sync/SyncTabsHeader";
import OperatorSyncCard from "../../../Components/data-sync/OperatorSyncCard";

const SyncSettings = () => {
  const [courseProgress, setCourseProgress] = useState([]);
  const [showZone, setShowZone] = useState(0);
  const pollIntervalRef = useRef(null);
  const completionMonitors = useRef({});

  const { data: progressData, refetch: refetchProgress } = useFetch({
    queryKey: ["coursesSyncProgress"],
    url: process.env.REACT_APP_API_URL + "Courses/CoursesSyncProgress",
    method: "GET",
  });

  useEffect(() => {
    if (!progressData) return;

    const data = progressData;
    setCourseProgress(data);

    if (data.length > 0) {
      const allDone = data.every((op) => op.progress === 100);
      if (allDone) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        toast.success("All sync processes finished");
      }
    }
  }, [progressData]);

  useEffect(() => {
    refetchProgress();
    const id = setInterval(() => {
      refetchProgress();
    }, 15000);
    pollIntervalRef.current = id;

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [refetchProgress]);

  const monitorBatchCompletion = (
    operatorKeys,
    completionMessage,
    monitorId,
  ) => {
    // Clear any existing monitor with this ID
    if (completionMonitors.current[monitorId]) {
      clearInterval(completionMonitors.current[monitorId]);
    }

    const checkInterval = setInterval(async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_API_URL + "Courses/CoursesSyncProgress",
        );
        const data = await response.json();

        // Check if all specified operators are completed
        const relevantOps = data.filter((op) =>
          operatorKeys.includes(op.operator),
        );

        if (relevantOps.length > 0) {
          const allCompleted = relevantOps.every(
            (op) => op.status === "Completed" && op.progress === 100,
          );

          if (allCompleted) {
            toast.success(completionMessage);
            clearInterval(checkInterval);
            delete completionMonitors.current[monitorId];
          }
        }
      } catch (error) {
        console.error("Error monitoring sync progress:", error);
      }
    }, 3000); // Check every 3 seconds

    completionMonitors.current[monitorId] = checkInterval;
  };

  // Helper function to check if an operator has failed
  const getOperatorStatus = (operatorKey) => {
    const operator = courseProgress.find((op) => op.operator === operatorKey);
    return operator || null;
  };

  const hasOperatorFailed = (operatorKey) => {
    const operator = getOperatorStatus(operatorKey);
    return operator && operator.status === "Failed";
  };

  // Render failed icon with tooltip
  const renderFailedIcon = (operatorKey, operatorName) => {
    const operator = getOperatorStatus(operatorKey);

    if (!operator) return null;

    // Check for status in multiple possible field names
    const statusValue =
      operator.status ||
      operator.statusCode ||
      operator.state ||
      operator.result;

    if (!statusValue) {
      console.warn(`No status field found for ${operatorKey}`);
      return null;
    }

    // Check for failure status (case-insensitive, multiple formats)
    const statusLower = String(statusValue).toLowerCase();
    const isFailed =
      statusLower === "failed" ||
      statusLower === "error" ||
      statusLower === "fail" ||
      statusValue === 400 ||
      statusValue === 500 ||
      (operator.progress === 100 &&
        statusLower !== "completed" &&
        statusLower !== "success");

    // console.log(
    //   `${operatorKey} - Status: ${statusValue}, isFailed: ${isFailed}`
    // );

    if (!isFailed) return null;

    return (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id={`tooltip-failed-${operatorKey}`}>
            <div className="text-start">
              <strong>Sync Failed</strong>
              <br />
              <small>
                {operatorName} synchronization encountered an error.
                <br />
                Status: {statusValue}
                <br />
                Duration: {operator.duration || "N/A"}
                <br />
                {operator.endTime && (
                  <>
                    Failed at: {moment(operator.endTime).format("HH:mm:ss")}
                    <br />
                  </>
                )}
                Please try syncing again or contact support if the issue
                persists.
              </small>
            </div>
          </Tooltip>
        }
      >
        <FaExclamationCircle
          style={{
            color: "#dc3545",
            marginLeft: "8px",
            cursor: "pointer",
            fontSize: "18px",
          }}
        />
      </OverlayTrigger>
    );
  };

  const syncGetMutation = useMutate({
    mutationKey: ["sync-get"],
    url: (vars) => process.env.REACT_APP_API_URL + vars.path,
    method: "GET",
    onMutate: (variables) => {
      if (variables?.startMessage) {
        toast.success(variables.startMessage);
      }
    },
    onSuccess: (_, variables) => {
      if (variables?.successMessage) {
        toast.success(variables.successMessage);
      }
      refetchProgress();
    },
    onError: (_, variables) => {
      if (variables?.errorMessage) {
        toast.error(variables.errorMessage);
      } else {
        toast.error("Failed to start sync process");
      }
    },
  });

  const syncPatchMutation = useMutate({
    mutationKey: ["sync-patch"],
    url: (vars) => process.env.REACT_APP_API_URL + vars.path,
    method: "PATCH",
    onMutate: (variables) => {
      if (variables?.startMessage) {
        toast.success(variables.startMessage);
      }
    },
    onSuccess: (_, variables) => {
      if (variables?.successMessage) {
        toast.success(variables.successMessage);
      }
      refetchProgress();
    },
    onError: (_, variables) => {
      if (variables?.errorMessage) {
        toast.error(variables.errorMessage);
      } else {
        toast.error("Failed to start sync process");
      }
    },
  });

  const syncPostMutation = useMutate({
    mutationKey: ["sync-post"],
    url: (vars) => process.env.REACT_APP_API_URL + vars.path,
    method: "POST",
    onMutate: (variables) => {
      if (variables?.startMessage) {
        toast.success(variables.startMessage);
      }
    },
    onSuccess: (_, variables) => {
      if (variables?.successMessage) {
        toast.success(variables.successMessage);
      }
      refetchProgress();
    },
    onError: (_, variables) => {
      if (variables?.errorMessage) {
        toast.error(variables.errorMessage);
      } else {
        toast.error("Failed to start sync process");
      }
    },
  });

  const ForceGlobal = (showMessages = true) => {
    syncPostMutation.mutate({
      path: "Sync/ForceGlobal",
      startMessage: showMessages
        ? "Global sync process initiated and syncing in progress - this may take a while"
        : null,
      // successMessage: showMessages ? "Global sync process completed" : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const syncCourse = (showMessages = true) => {
    syncGetMutation.mutate({
      path: "Courses/SyncCourses",
      startMessage: showMessages ? "Courses sync process initiated" : null,
      // successMessage: showMessages ? "Courses sync process completed" : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const syncCSLPStudents = (showMessages = true) => {
    syncGetMutation.mutate({
      path: "StudentsCSLP/SyncCSLPStudents/",
      startMessage: showMessages
        ? "CSLP Students sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "CSLP Students sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const syncCSLPTeachers = (showMessages = true) => {
    syncGetMutation.mutate({
      path: "TeachersCSLP/SyncCSLPTeachers/",
      startMessage: showMessages
        ? "CSLP Teachers sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "CSLP Teachers sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const syncCourseAssignment = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncCourseAssignments/${cs}`,
      startMessage: showMessages
        ? "Course Assignments sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Course Assignments sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncCourseAssignment = () => {
    toast.success("Course Assignments sync initiated");
    syncCourseAssignment("CS2", false);
    monitorBatchCompletion(
      ["Assignments:CS2"],
      "Course Assignments sync completed",
      "batchCourseAssignments",
    );
  };

  const SyncCourseAssignmentsSubmissions = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncCourseAssignmentsSubmissions/${cs}`,
      startMessage: showMessages
        ? "Assignment Submissions sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Assignment Submissions sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncCourseAssignmentsSubmissions = () => {
    toast.success("Assignment Submissions sync initiated");
    SyncCourseAssignmentsSubmissions("CS2", false);
    monitorBatchCompletion(
      ["Submissions:CS2"],
      "Assignment Submissions sync completed",
      "batchCourseSubmissions",
    );
  };

  const SyncStudentsFromCourses = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncStudentsFromCourses/${cs}`,
      startMessage: showMessages
        ? "Course Students sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Course Students sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncStudentsFromCourses = () => {
    toast.success("Course Students sync initiated");
    SyncStudentsFromCourses("CS2", false);
    monitorBatchCompletion(
      ["CourseUsers3:CS2"],
      "Course Students sync completed",
      "batchCourseStudents",
    );
  };

  const SyncTeachersFromCourses = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncTeachersFromCourses/${cs}`,
      startMessage: showMessages
        ? "Course Teachers sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Course Teachers sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncTeachersFromCourses = () => {
    toast.success("Course Teachers sync initiated");
    SyncTeachersFromCourses("CS2", false);
    monitorBatchCompletion(
      ["CourseUsers4:CS2"],
      "Course Teachers sync completed",
      "batchCourseTeachers",
    );
  };

  const syncCourseTeacherEnrollments = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncCourseTeacherEnrollments/${cs}`,
      startMessage: showMessages
        ? "Course Teacher Enrollments sync process initiated"
        : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncCourseTeacherEnrollments = () => {
    toast.success("Course Teacher Enrollments sync initiated");
    syncCourseTeacherEnrollments("CS2", false);
    monitorBatchCompletion(
      ["CourseTeacherEnrollments:CS2"],
      "Course Teacher Enrollments sync completed",
      "batchCourseTeacherEnrollments",
    );
  };

  const syncCourseAnnouncements = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncCourseAnnouncements/${cs}`,
      startMessage: showMessages
        ? "Course Announcements sync process initiated"
        : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const syncLearningOutcomes = (cs, showMessages = true) => {
    syncGetMutation.mutate({
      path: `Courses/SyncLearningOutcomes/${cs}`,
      startMessage: showMessages
        ? "Learning Outcomes sync process initiated"
        : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncLearningOutcomes = () => {
    toast.success("Learning Outcomes sync initiated");
    syncLearningOutcomes("CS2", false);
    monitorBatchCompletion(
      ["LearningOutcomes:CS2"],
      "Learning Outcomes sync completed",
      "batchLearningOutcomes",
    );
  };

  const batchSyncCourseAnnouncements = () => {
    toast.success("Course Announcements sync initiated");
    syncCourseAnnouncements("CS2", false);
    monitorBatchCompletion(
      ["CourseAnnouncements:CS2"],
      "Course Announcements sync completed",
      "batchCourseAnnouncements",
    );
  };

  const syncStudentsLastLogin = (cs, showMessages = true) => {
    syncPatchMutation.mutate({
      path: `Users/SyncStudentsLastLogin/${cs}`,
      startMessage: showMessages
        ? "Students Status sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Students Status sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncStudentsLastLogin = () => {
    toast.success("Students Status sync initiated");
    syncStudentsLastLogin("CS2", false);
    monitorBatchCompletion(
      ["StudentsLastLogin:CS2"],
      "Students Status sync completed",
      "batchStudentsStatus",
    );
  };

  const syncTeachersLastLogin = (cs, showMessages = true) => {
    syncPatchMutation.mutate({
      path: `Users/SyncTeachersLastLogin/${cs}`,
      startMessage: showMessages
        ? "Teachers Status sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Teachers Status sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncTeachersLastLogin = () => {
    toast.success("Teachers Status sync initiated");
    syncTeachersLastLogin("CS2", false);
    monitorBatchCompletion(
      ["TeachersLastLogin:CS2"],
      "Teachers Status sync completed",
      "batchTeachersStatus",
    );
  };

  const syncSyncStudentsLastActivity = (cs, showMessages = true) => {
    syncPatchMutation.mutate({
      path: `Users/SyncStudentsLastActivity/${cs}`,
      startMessage: showMessages
        ? "Students Last Request sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Students Last Request sync process completed"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncStudentsLastActivity = () => {
    toast.success("Students Last Request sync initiated");
    syncSyncStudentsLastActivity("CS2", false);
    monitorBatchCompletion(
      ["SyncStudentsLastActivity:CS2"],
      "Students Last Request sync completed",
      "batchStudentsLastActivity",
    );
  };

  const syncTeacherLastRequest = (cs, showMessages = true) => {
    syncPatchMutation.mutate({
      path: `Users/SyncTeachersLastActivity/${cs}`,
      startMessage: showMessages
        ? "Teacher Last Request Sync process initiated"
        : null,
      // successMessage: showMessages
      //   ? "Teacher Last Request sync processes finished"
      //   : null,
      errorMessage: showMessages ? "Failed to start sync process" : null,
    });
  };

  const batchSyncTeacherLastRequest = () => {
    toast.success("Teacher Last Request sync initiated");
    syncTeacherLastRequest("CS2", false);
    monitorBatchCompletion(
      ["SyncTeachersLastActivity:CS2"],
      "Teacher Last Request sync completed",
      "batchTeachersLastActivity",
    );
  };

  const getGlobal = () => {
    // toast.success("Global sync initiated for all processes");

    ForceGlobal(true);

    // Trigger all sync operations with suppressed messages - CS2 ONLY
    // syncCourse(false);
    // syncCourseAssignment("CS2", false);
    // SyncCourseAssignmentsSubmissions("CS2", false);
    // SyncStudentsFromCourses("CS2", false);
    // SyncTeachersFromCourses("CS2", false);
    // syncCSLPStudents(false);
    // syncCSLPTeachers(false);
    // syncStudentsLastLogin("CS2", false);
    // syncTeachersLastLogin("CS2", false);
    // syncSyncStudentsLastActivity("CS2", false);
    // syncTeacherLastRequest("CS2", false);

    // Monitor all global sync operations with real-time progress tracking - CS2 ONLY
    monitorBatchCompletion(
      [
        "Courses:CS2",
        "Assignments:CS2",
        "Submissions:CS2",
        "CourseUsers3:CS2",
        "CourseUsers4:CS2",
        "CourseTeacherEnrollments:CS2",
        "CourseAnnouncements:CS2",
        "CSLP:Students",
        "CSLP:Teachers",
        // "LearningOutcomes:CS2",
        "StudentsLastLogin:CS2",
        // "SyncTeachersLastLogin:CS2",
        "TeachersLastLogin:CS2",
        "SyncStudentsLastActivity:CS2",
        "SyncTeachersLastActivity:CS2",
      ],
      "Global sync completed for all processes",
      "globalSync",
    );
  };

  return (
    <div className="api-sync">
      <div className="container-fluid mt-10">
        <SyncTabsHeader
          showZone={showZone}
          setShowZone={setShowZone}
          onGlobalSync={getGlobal}
        />
        <div className="row">
          <div className="col-md-12">
            <hr></hr>
          </div>
        </div>

        <div className="row mt-30">
          {showZone != 2 && showZone != 3 && (
            <>
              {/* Courses */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Courses
                      {renderFailedIcon("Courses:CS2", "Courses")}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={() => syncCourse()}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="Courses:CS2"
                        courseProgress={courseProgress}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Students */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Course Students
                      {renderFailedIcon("CourseUsers3:CS2", "Course Students")}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncStudentsFromCourses}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="CourseUsers3:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => SyncStudentsFromCourses("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Teachers */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Course Teachers
                      {renderFailedIcon("CourseUsers4:CS2", "Course Teachers")}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncTeachersFromCourses}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="CourseUsers4:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => SyncTeachersFromCourses("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Teacher Enrollments */}
              <div className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Course Teacher Enrollments
                      {renderFailedIcon(
                        "CourseTeacherEnrollments:CS2",
                        "Course Teacher Enrollments",
                      )}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncCourseTeacherEnrollments}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="CourseTeacherEnrollments:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => syncCourseTeacherEnrollments("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Announcements */}
              <div className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Course Announcements
                      {renderFailedIcon(
                        "CourseAnnouncements:CS2",
                        "Course Announcements",
                      )}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncCourseAnnouncements}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="CourseAnnouncements:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => syncCourseAnnouncements("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Assignments */}
              <div className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Course Assignments
                      {renderFailedIcon(
                        "Assignments:CS2",
                        "Course Assignments",
                      )}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncCourseAssignment}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="Assignments:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => syncCourseAssignment("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignment Submissions */}
              <div className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Assignment Submissions
                      {renderFailedIcon(
                        "Submissions:CS2",
                        "Assignment Submissions",
                      )}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncCourseAssignmentsSubmissions}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="Submissions:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() =>
                          SyncCourseAssignmentsSubmissions("CS2")
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Learning Outcomes */}
              {/* <div className="col-md-6 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      Learning Outcomes
                      {renderFailedIcon(
                        "LearningOutcomes:CS2",
                        "Learning Outcomes",
                      )}
                    </h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={batchSyncLearningOutcomes}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <OperatorSyncCard
                        title="Taaleem"
                        operatorKey="LearningOutcomes:CS2"
                        courseProgress={courseProgress}
                        onInlineSync={() => syncLearningOutcomes("CS2")}
                      />
                    </div>
                  </div>
                </div>
              </div> */}
            </>
          )}

          {showZone != 1 && showZone != 3 && (
            <>
              {/* CSLP Students & Teachers */}
              <div className="col-md-12 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">CSLP Students & Teachers</h3>
                    <div className="align-self-center">
                      <button
                        className="btn btn-sm btn-light mr-5"
                        onClick={() => {
                          syncCSLPStudents();
                          syncCSLPTeachers();
                        }}
                      >
                        <FaSyncAlt /> &nbsp; Sync
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <OperatorSyncCard
                          title="Students"
                          operatorKey="CSLP:Students"
                          courseProgress={courseProgress}
                          onInlineSync={() => syncCSLPStudents()}
                        />
                      </div>
                      <div className="col-md-6">
                        <OperatorSyncCard
                          title="Teachers"
                          operatorKey="CSLP:Teachers"
                          courseProgress={courseProgress}
                          onInlineSync={() => syncCSLPTeachers()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {showZone != 1 && showZone != 2 && (
            <>
              {/* Students Status + Students Last Request */}
              <div className="col-md-12 mb-3">
                <div className="row">
                  {/* Students Status */}
                  <div className="col">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Students Status
                          {renderFailedIcon(
                            "StudentsLastLogin:CS2",
                            "Students Status",
                          )}
                        </h3>
                        <div className="align-self-center">
                          <button
                            className="btn btn-sm btn-light mr-5"
                            onClick={batchSyncStudentsLastLogin}
                          >
                            <FaSyncAlt /> &nbsp; Sync
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <OperatorSyncCard
                            title="Taaleem"
                            operatorKey="StudentsLastLogin:CS2"
                            courseProgress={courseProgress}
                            onInlineSync={() => syncStudentsLastLogin("CS2")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Students Last Request */}
                  <div className="col">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Students Last Request
                          {renderFailedIcon(
                            "SyncStudentsLastActivity:CS2",
                            "Students Last Request",
                          )}
                        </h3>
                        <div className="align-self-center">
                          <button
                            className="btn btn-sm btn-light mr-5"
                            onClick={batchSyncStudentsLastActivity}
                          >
                            <FaSyncAlt /> &nbsp; Sync
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <OperatorSyncCard
                            title="Taaleem"
                            operatorKey="SyncStudentsLastActivity:CS2"
                            courseProgress={courseProgress}
                            onInlineSync={() =>
                              syncSyncStudentsLastActivity("CS2")
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teachers Status + Teachers Last Request */}
              <div className="col-md-12 mb-3">
                <div className="row">
                  {/* Teachers Status */}
                  <div className="col">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Teachers Status
                          {renderFailedIcon(
                            "TeachersLastLogin:CS2",
                            "Teachers Status",
                          )}
                        </h3>
                        <div className="align-self-center">
                          <button
                            className="btn btn-sm btn-light mr-5"
                            onClick={batchSyncTeachersLastLogin}
                          >
                            <FaSyncAlt /> &nbsp; Sync
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <OperatorSyncCard
                            title="Taaleem"
                            operatorKey="TeachersLastLogin:CS2"
                            courseProgress={courseProgress}
                            onInlineSync={() => syncTeachersLastLogin("CS2")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Teachers Last Request */}
                  <div className="col">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Teachers Last Request
                          {renderFailedIcon(
                            "SyncTeachersLastActivity:CS2",
                            "Teachers Last Request",
                          )}
                        </h3>
                        <div className="align-self-center">
                          <button
                            className="btn btn-sm btn-light mr-5"
                            onClick={batchSyncTeacherLastRequest}
                          >
                            <FaSyncAlt /> &nbsp; Sync
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <OperatorSyncCard
                            title="Taaleem"
                            operatorKey="SyncTeachersLastActivity:CS2"
                            courseProgress={courseProgress}
                            onInlineSync={() => syncTeacherLastRequest("CS2")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncSettings;
